"use client";
import { useCallback, useEffect, useMemo, useState } from "react"
import { DataTable } from "../../dataTables/data-table"
import { deteleProducts, fetchProducts } from "@/apiCalls/apiCalls"
import { Button } from "@/components/ui/button";
import { Products, productsColumns } from "@/pages/products/ProductsColumns"
import Spinner from "../spinnner/Spinner"
import CreateAddProductsDrawer from "./CreateAddProductsDrawer"
import ProductUpdateDrawer from "./ProductUpdateDrawer";
import { errorNotification, successNotification } from "@/utils/Notification"
import { jwtDecode } from "jwt-decode"
import useAuth from "@/hooks/useAuth";
import { ProductsDataTables } from "./ProductsDataTables";
import { useLocation, useNavigate } from "react-router-dom";
import useAxiosPrivate from "@/context/UseAxiosPrivate";


export default function ProductsFn() {
    const [data, setData] = useState<Products[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [showAddProductsDrawer, setShowAddProductsDrawer] = useState(false)
    const [showUpdateProductsDrawer, setShowUpdateProductsDrawer] = useState(false)
    const [productData, setProductData] = useState<Products | undefined>(undefined)
    const { auth } = useAuth()
    let token: any = auth.accessToken
    const decoded: any = jwtDecode(token);
    let roles: string[] = decoded.UserInfo.roles
    const navigate = useNavigate();
    const location = useLocation();
    const axiosPrivate = useAxiosPrivate()

    const getProducts = () => {
        setLoading(true)
        let isMounted = true;
        const controller = new AbortController()
        axiosPrivate.get('/api/v1/products', {
            signal: controller.signal
        }).then((response: any) => response.data.data)
            .then((data: Products[]) => {
                if (isMounted) {
                    setData(data)
                }
            }).catch((err: any) => {
                const errors = err?.response?.data?.errors;
                if (errors && errors?.[0]?.message === "token as expired") {
                    let errror = errors?.[0]?.message
                    errorNotification(
                        "There was an issue",
                        `${errror}`,
                        'topRight'
                    );
                    navigate('/', { state: { from: location }, replace: true });
                } else if (errors && Array.isArray(errors) && errors.length > 0) {
                    errors.forEach((error: any) => {
                        const errorMessage = error.message || "An unknown error occurred";
                        errorNotification(
                            "There was an issue",
                            `${errorMessage}`,
                            'topRight'
                        );
                    });
                } else {
                    errorNotification(
                        "There was an issue",
                        "No server reponse",
                        'topRight'
                    );
                    navigate('/', { state: { from: location }, replace: true });
                }
            }).finally(() => {
                setLoading(false)
            })
        return () => {
            isMounted = false;
            controller.abort();
        };
    };

    useEffect(() => {
        getProducts()
    }, []);


    const deleteProductById = (productId: any) => {
        let isMounted = true;
        const controller = new AbortController()
        axiosPrivate.delete(`/api/v1/products/${productId}`, {
            signal: controller.signal
        }).then((response: any) => response.data.data)
            .then((data: any) => {
                if (isMounted) {
                    successNotification(
                        "Product successfully deleted",
                        `Product with id ${productId} was deleted`,
                        'topRight'
                    );

                }
                getProducts()
            }).catch((err: any) => {
                const errors = err?.response?.data?.errors;
                if (errors && errors?.[0]?.message === "token as expired") {
                    let errror = errors?.[0]?.message
                    errorNotification(
                        "There was an issue",
                        `${errror}`,
                        'topRight'
                    );
                    navigate('/', { state: { from: location }, replace: true });
                } else if (errors && Array.isArray(errors) && errors.length > 0) {
                    errors.forEach((error: any) => {
                        const errorMessage = error.message || "An unknown error occurred";
                        errorNotification(
                            "There was an issue",
                            `${errorMessage}`,
                            'topRight'
                        );
                    });
                } else {
                    errorNotification(
                        "There was an issue",
                        "No server reponse",
                        'topRight'
                    );
                    navigate('/', { state: { from: location }, replace: true });
                }
            })
        return () => {
            isMounted = false;
            controller.abort();
        };
    };


    const editProductFunctionCall = (products: Products) => {
        setProductData(products)
        setShowUpdateProductsDrawer(!showUpdateProductsDrawer)
    }
    
    const deleteProductFunctionCall = (products: Products) => {
        deleteProductById(products.id)
    }

    const onDelete = useCallback(
        (products: Products) => deleteProductFunctionCall(products),
        []
    )

    const onEdit = useCallback(
        (products: Products) => editProductFunctionCall(products),
        []
    )


    const productsColumnsData = useMemo(() => productsColumns({ onEdit, onDelete, roles }), [])
    if (loading) {
        return <Spinner />;
    }

    return (
        <main className='mx-3 mt-16 sm:ml-[300px] sm:mt-3'>

            {roles.includes('product-create') && (
                <Button
                    variant='default'
                    className="flex items-center justify-end"
                    onClick={() => setShowAddProductsDrawer(!showAddProductsDrawer)}
                >
                    Create products
                </Button>
            )}
            <ProductsDataTables columns={productsColumnsData} data={data} filterNameColumn="uuid" />

            {roles.includes('product-create') && (
                <CreateAddProductsDrawer
                    showAddProductsDrawer={showAddProductsDrawer}
                    setShowAddProductsDrawer={setShowAddProductsDrawer}
                    getProducts={getProducts}
                />
            )}

            {productData && roles.includes('product-edit') && (
                <ProductUpdateDrawer
                    showUpdateProductsDrawer={showUpdateProductsDrawer}
                    setShowUpdateProductsDrawer={setShowUpdateProductsDrawer}
                    products={productData}
                    getProducts={getProducts}
                    setProductData={setProductData}
                />
            )}

        </main>
    )
}